import { useState, useMemo, useCallback } from "react";
import { Button, Input } from "@aidkitorg/component-library";
import { usePost } from "../../../API";
import { useLocalizedStrings } from "../../../Localization";
import { useConnectionModal } from "./ConnectionModalContext";
import { SourceHeader } from "./SourceHeader";
import { type ConnectionModalState } from "./types";

// Temporary - Deck is working on including this information with their API
const FIELDS = [
  { field_name: "username", field_type: "Username", field_label: "Username" },
  { field_name: "password", field_type: "Password", field_label: "Password" },
];

interface ConnectionModalSourcePageProps {
  state: ConnectionModalState & { stage: "source" };
}

export function ConnectionModalSourcePage({
  state,
}: ConnectionModalSourcePageProps) {
  const L = useLocalizedStrings();
  const connectToSource = usePost("/deck/connect_to_source", { suppressErrorDisplay: true });
  const { getLocalizedString, onUpdateState } = useConnectionModal();

  const [selectedSourceFields, setSelectedSourceFields] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const hasFilledOutAllFields = useMemo(() => {
    return FIELDS.every((field) => !!selectedSourceFields[field.field_name]);
  }, [state, selectedSourceFields]);

  const handleUpdateSourceField = useCallback((fieldName: string, fieldValue: string) => {
    setSelectedSourceFields((selectedSourceFields) => ({ ...selectedSourceFields, [fieldName]: fieldValue }));
  }, []);

  const handleClickGoBack = useCallback(() => {
    onUpdateState({ stage: "search" });
  }, [onUpdateState]);

  const handleClickSubmit = useCallback(async () => {
    setIsSubmitting(true);
    const jobGuid = await connectToSource({
      username: selectedSourceFields["username"],
      password: selectedSourceFields["password"],
      source_guid: state.selectedSource.id,
    });

    if (typeof jobGuid === 'string') {
      onUpdateState({
        stage: "pending",
        selectedSource: state.selectedSource,
        jobGuid,
      });
    } else {
      onUpdateState({
        stage: "error",
        selectedSource: state.selectedSource,
        errorMessage: getLocalizedString("genericErrorMessage", "generic_error_message"),
      });
    }
  }, [state, selectedSourceFields]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && hasFilledOutAllFields) {
      handleClickSubmit();
    }
  }, [handleClickSubmit, hasFilledOutAllFields]);

  return <div>
    <SourceHeader source={state.selectedSource} />
    <fieldset>
      {FIELDS.map((field) =>
        <div key={field.field_label} className="mt-3">
          <Input
            label={field.field_label}
            value={selectedSourceFields[field.field_name] || ""}
            type={field.field_type === "Password" ? "password" : "text"}
            onChange={(e) => handleUpdateSourceField(field.field_name, e.currentTarget.value)}
            onKeyDown={handleKeyDown}
            dir="ltr" // These fields will always be in English
            required
          />
        </div>
      )}
    </fieldset>
    <div className="mt-6 flex justify-between">
      <Button onClick={handleClickGoBack} variant="secondary">
        {getLocalizedString("returnToSearchPageText", "return_to_search_page")}
      </Button>
      <Button
        onClick={handleClickSubmit}
        isDisabled={!hasFilledOutAllFields}
        loading={isSubmitting}
        title={!hasFilledOutAllFields ? L.questions.deck_verification.connection_modal.required_fields_missing : undefined}
        variant="primary"
      >
        {L.submission.submit}
      </Button>
    </div>
  </div>;
}
